import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '../lib/services/api'
import { CRISIS_ENDPOINTS } from './endpoints'

// Crisis types
export interface Crisis {
  id: string
  type: string
  location: string
  description: string
  status: string
  createdAt: string | null
  closedAt: string | null
  duration: number | null
  created_by: {
    id: string
    name: string
  } | null
}

export interface CrisisListResponse {
  data: Crisis[]
}

export interface CreateCrisisData {
  type: string
  location: string
  description: string
  practiceCaseId?: string | null
}

export interface UpdateCrisisData {
  type?: string
  location?: string
  description?: string
}

// Hook to fetch all crises
export const useCrises = () => {
  return useQuery({
    queryKey: ['crises'],
    queryFn: async (): Promise<CrisisListResponse> => {
      return apiService.get(CRISIS_ENDPOINTS.LIST)
    },
  })
}

// Hook to fetch active crisis
export const useActiveCrisis = () => {
  return useQuery({
    queryKey: ['active-crisis'],
    queryFn: async (): Promise<{ data: Crisis | null }> => {
      return apiService.get(CRISIS_ENDPOINTS.ACTIVE)
    },
  })
}

// Hook to fetch single crisis by ID
export const useCrisis = (crisisId: string) => {
  return useQuery({
    queryKey: ['crisis', crisisId],
    queryFn: async (): Promise<{ data: Crisis }> => {
      return apiService.get(CRISIS_ENDPOINTS.CRISIS_BY_ID(crisisId))
    },
    enabled: !!crisisId,
  })
}

// Hook to create crisis
export const useCreateCrisis = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateCrisisData): Promise<{ data: Crisis }> => {
      return apiService.post(CRISIS_ENDPOINTS.LIST, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crises'] })
      queryClient.invalidateQueries({ queryKey: ['active-crisis'] })
    },
  })
}

// Hook to update crisis
export const useUpdateCrisis = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      crisisId,
      data,
    }: {
      crisisId: string
      data: UpdateCrisisData
    }): Promise<{ data: Crisis }> => {
      return apiService.put(CRISIS_ENDPOINTS.CRISIS_BY_ID(crisisId), data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crises'] })
      queryClient.invalidateQueries({ queryKey: ['active-crisis'] })
      queryClient.invalidateQueries({ queryKey: ['crisis'] })
    },
  })
}

// Hook to close crisis
export const useCloseCrisis = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (crisisId: string): Promise<{ data: { id: string; closed: boolean } }> => {
      return apiService.delete(CRISIS_ENDPOINTS.CRISIS_BY_ID(crisisId))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crises'] })
      queryClient.invalidateQueries({ queryKey: ['active-crisis'] })
    },
  })
}

export const useDeleteCrisis = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (crisisId: string): Promise<{ data: { id: string; deleted: boolean } }> => {
      return apiService.post(CRISIS_ENDPOINTS.DELETE(crisisId), {})
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crises'] })
      queryClient.invalidateQueries({ queryKey: ['active-crisis'] })
    },
  })
}
